import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import omnichatLogo from '@/assets/omnichat-logo.png';
import { Switch } from '@/components/ui/Switch';
import { useUpdateAudience } from '@/hooks/useUpdateAudience';
import {
  Audience,
  AudiencePlatformStatus,
  EAudiencePlatform,
  UpdateAudienceResponse,
} from '@/services/AudiencesService';

interface OmnichatSwitchProps {
  audience: Pick<Audience, 'id' | 'audiencePlatform'>;
}

export function OmnichatSwitch({ audience }: OmnichatSwitchProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Behaviors.AudiencesCreate.components.OmnichatSwitch',
  });

  const { updateAudience, isLoading } = useUpdateAudience(audience.id);

  const omnichatAudience = audience.audiencePlatform.find(
    (audiencePlatform) =>
      audiencePlatform.platform === EAudiencePlatform.OMNICHAT,
  );

  const [localPlatformData, setLocalPlatformData] =
    useState<UpdateAudienceResponse | null>(null);

  const platformData = localPlatformData ?? omnichatAudience;
  const isSynchronized = platformData?.isSynchronized || false;
  const status = platformData?.status;
  const isPending = status === AudiencePlatformStatus.PENDING && isSynchronized;
  const isReady = status === AudiencePlatformStatus.READY;
  const isError = status === AudiencePlatformStatus.ERROR;
  const [checked, setChecked] = useState(isSynchronized);

  async function handleCheckedChange() {
    if (isSynchronized && isReady) {
      toast.error(t('toast.alreadySynchronized'));
      return;
    }

    try {
      const result = await updateAudience({
        platform: EAudiencePlatform.OMNICHAT,
      });

      setLocalPlatformData(result);
      setChecked(true);
      toast.success(t('toast.success'));
    } catch (error) {
      setChecked(false);
      if (error instanceof AxiosError && error.response?.status === 400) {
        toast.error(t('toast.error'));
        return;
      }
      toast.error(t('toast.errorUnexpected'));
    }
  }

  return (
    <div className="flex items-center justify-between">
      <div className="flex cursor-default items-center gap-2 py-5">
        <img
          src={omnichatLogo}
          alt="Omnichat Logo"
          className="w-30 h-8 object-contain"
        />
      </div>

      <div className="flex items-center gap-4">
        {isPending && (
          <div className="pointer-events-none">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
        <Switch
          checked={checked}
          onCheckedChange={handleCheckedChange}
          disabled={isLoading || isPending || isError}
          className={
            isReady
              ? 'data-[state=checked]:bg-black'
              : isPending
                ? 'data-[state=checked]:bg-gray-600'
                : undefined
          }
        />
      </div>
    </div>
  );
}
